<script setup>
import Loading from '@/components/NLoading.vue';
import Datepicker from '@/components/form/Datepicker.vue';
import RadioGroup from '@/components/form/RadioGroup.vue';
import Radio from '@/components/form/Radio.vue';
import Button from '@/components/ui/button/index.vue';
import CategoriesPicker from '@/components/game/CategoriesPicker.vue';
import StepImport from "@/components/forum-tools/Import.vue";
import StepInvite from "@/components/forum-tools/Invite.vue";
import { useChannel } from '@/composable/pusher';
import { Icon } from '@bignerve/ui-core';
import { api } from '@/modules/services';
import { ref } from 'vue';
import { kebabCase } from 'lodash'
import { CHANNEL_SCOPE, CHANNEL_MODE, ACCESS_LEVEL } from '@/constant/channel';

const props = defineProps({
    channel: {
        type: Object,
        required: true
    },
    platform: {
        type: Object,
        default: () => ({})
    },
});

const saved = ref(false);
const updating = ref(false);
const unlocked = ref(false);
const data = ref(props.channel);
const ingestion = ref(null);

const emit = defineEmits(['update']);
const { listen } = useChannel(`rss-feed-${props.channel.id}`);

const handleUpdateChannel = async (channel) => {
    saved.value = false;

    if (!props.platform?.slug || !channel?.slug) {
        return;
    }

    try {
        updating.value = true;

        await api.channel.update(props.platform.slug, channel.slug, channel);

        saved.value = true;
        updating.value = false;

        setTimeout(() => {
            saved.value = false;
        }, 5000);
    } catch (error) {
        updating.value = false;

        console.error(error);
    }
}

const handleChangeCategory = (target) => {
    if (!target) {
        return;
    }

    // update the category info
    data.value.name = data.value.name || target.name;
    data.value.category_id = data.value.category_id || target.id;
    data.value.icon = target.icon;
    data.value.slug = data.value.slug || kebabCase(target.name)

    handleUpdateChannel(data.value);
}

const handleUnlock = () => {
    unlocked.value = true;
}

const handleUpdateFeed = async () => {
    data.value.metadata.rss_feed.active = true;
    data.value.metadata.rss_feed.enabled = true;
    data.value.metadata.rss_feed.validated = true;
    
    await handleUpdateChannel(data.value);
}

const handleEnableFeed = async () => {
    if (!data.value.metadata.rss_feed.enabled) {
        data.value.metadata.rss_feed.active = false;
        data.value.metadata.access_level = ACCESS_LEVEL.WRITE;
    }

    if (data.value.metadata.rss_feed.enabled) {
        data.value.metadata.rss_feed.validated = true;
        data.value.access_level = ACCESS_LEVEL.READONLY;
    }
    
    await handleUpdateChannel(data.value);
}

const handleToggleFeedStatus = async () => {
    data.value.metadata.rss_feed.active = !data.value.metadata.rss_feed.active;

    await handleUpdateChannel(data.value);
}

listen('feed-processing', (payload) => {
    ingestion.value = payload;
})
</script>

<template>
    <div class="flex items-start gap-8 flex-col sm:flex-row w-full">
        <CategoriesPicker :initialValue="data.metadata?.category" @change="handleChangeCategory" :allowAll="false">
            <template #activator="{ on }">
                <Button v-on="on" class="h-10 gap-2 font-bold shadow-active rounded-xl min-w-[180px]" type="button">
                    <img :src="`/img/category-icons/${data.icon}`" :alt="data.name" class="h-4 filter invert-[70%]" @error="e => e.target.src = `/img/category-icons/article-draft-icon.svg`">
                    {{ data.name }}
                </Button>
            </template>
        </CategoriesPicker>
        <div class="grid gap-2 flex-1">
            <RadioGroup v-model="data.channel_mode" :name="`${data.id}:channel_mode`" @change="handleUpdateChannel(data)">
                <div class="grid mb-2 gap-1">
                    <div class="flex gap-2 items-center text-sm">
                        <Radio 
                            label="Public" 
                            :disabled="data.channel_mode === CHANNEL_MODE.PRIVATE && !unlocked"
                            :modelValue="CHANNEL_MODE.PUBLIC"
                            :class="{
                                'font-bold': data.channel_mode === CHANNEL_MODE.PUBLIC,
                                'disabled': data.channel_mode === CHANNEL_MODE.PRIVATE && !unlocked
                            }"
                        />
                        <span v-if="saved && data.channel_mode === CHANNEL_MODE.PUBLIC" class="italic text-nerve">
                            Saved… your custom channel is now live in your <RouterLink :to="{ name: 'discussion-forums.channel', params: { platformSlug: platform?.slug || 'platform', slug: channel.slug } }" class="underline text-inherit">forum</RouterLink>.
                        </span>
                    </div>
                    <div class="flex items-center text-sm">
                        <Radio label="Private" :modelValue="CHANNEL_MODE.PRIVATE" :class="{ 'font-bold': data.channel_mode === CHANNEL_MODE.PRIVATE }" />
                        <Button 
                            v-if="data.channel_mode === CHANNEL_MODE.PRIVATE"
                            v-tooltip="{
                                content: unlocked 
                                    ? `<div style='max-width: 240px;'>Channel unlocked. You can now change it to Public. This will make all content visible to everyone.</div>`
                                    : `<div style='max-width: 240px;'>Tap the lock if you want to change this Private channel to Public. All existing content will be revealed.</div>`,
                                html: true
                            }" 
                            variant="icon" 
                            class="-ml-2" 
                            @click="handleUnlock"
                        >
                            <Icon :as="unlocked ? 'unlock-solid' : 'lock-solid'" />
                        </Button>
                        <span v-if="saved && data.channel_mode === CHANNEL_MODE.PRIVATE"  class="italic text-nerve">
                            Saved… your custom channel is now Private.
                        </span>
                    </div>
                </div>
            </RadioGroup>

            <div v-if="data.channel_mode === CHANNEL_MODE.PRIVATE" class="sm:ml-4 grid gap-2">
                <RadioGroup v-model="data.channel_scope" :name="`${data.id}:channel_scope`" @change="handleUpdateChannel(data)">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        <Radio label="Members only (free)" :modelValue="CHANNEL_SCOPE.MEMBERS_ONLY_FREE" :class="{ 'font-bold': data.channel_scope === CHANNEL_SCOPE.MEMBERS_ONLY_FREE }" />
                        <Radio label="Bignerve subscribers" :modelValue="CHANNEL_SCOPE.BIGNERVE_SUBSCRIBERS" :class="{ 'font-bold': data.channel_scope === CHANNEL_SCOPE.BIGNERVE_SUBSCRIBERS }" />
                        <Radio label="Members only (paid)" :modelValue="CHANNEL_SCOPE.MEMBERS_ONLY_PAID" :class="{ 'font-bold': data.channel_scope === CHANNEL_SCOPE.MEMBERS_ONLY_PAID }" />
                        <Radio label="Minimum Commentator Score" :modelValue="CHANNEL_SCOPE.MINIMUM_COMMENTATOR_SCORE" :class="{ 'font-bold': data.channel_scope === CHANNEL_SCOPE.MINIMUM_COMMENTATOR_SCORE }" />
                    </div>
                </RadioGroup>
                
                <div class="border-b w-full my-2"></div>

                <StepImport :platform="platform" :channel="data" @change="handleUpdateChannel" />

                <StepInvite :platform="platform" :channel="data" @change="handleUpdateChannel" /> 

                <div class="border-b w-full my-2"></div>
            </div>

            <div v-if="data.metadata.rss_feed" class="sm:ml-4 grid gap-4">
                <div class="flex flex-col sm:flex-row gap-2 items-start">
                    <label class="flex items-center gap-2 font-bold min-w-[8rem] my-2">
                        <input 
                            type="checkbox" 
                            v-model="data.metadata.rss_feed.enabled"
                            @change="handleEnableFeed"
                            class="h-4 w-4 text-nerve border-gray-300 rounded focus:ring-nerve"
                        >
                        RSS Feed
                    </label>

                    <div v-if="data.metadata.rss_feed.enabled" class="grid gap-2">
                        <div class="flex items-center gap-2">
                            <input 
                                type="text"
                                v-model="data.metadata.rss_feed.source"
                                class="flex-1 px-3 py-2 border rounded-md"
                                placeholder="https://example.com/rss"
                            >
                        </div>

                        <div class="flex items-center justify-between gap-2">
                            <div class="flex gap-2 items-center flex-col sm:flex-row">
                                <span class="whitespace-nowrap italic">How far back to retrieve:</span>
                                <Datepicker v-model="data.metadata.rss_feed.retrieve_from" input-class="border-none bg-transparent" format="F j, Y" />
                            </div>
                            <Button
                                v-if="!['processing', 'started'].includes(ingestion?.status)"
                                variant="ghost"
                                class="gap-1"
                                @click="handleToggleFeedStatus"
                            >
                                <Icon :as="data.metadata.rss_feed.active ? 'close-circle' : 'play-circle-regular'" />
                                {{ data.metadata.rss_feed.active ? 'Stop' : 'Resume' }}
                            </Button>
                        </div>

                        <div class="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <Button variant="primary" class="gap-1"
                                @click="handleUpdateFeed">
                                <Loading v-if="updating" class="text-white" active />
                                {{ data.metadata.rss_feed.active && ingestion?.status === 'completed' ? 'Feed ingested' : 'Retrieve Feed' }}
                            </Button>
                            <div class="flex-1 flex items-center gap-2">
                                <div class="flex items-center gap-1 text-sm">
                                    <Icon as="check-circle-solid" :class="data.metadata.rss_feed.validated ? 'text-green-600' : 'text-dust-500'" />
                                    <span>Validated.</span>
                                </div>
                                <a :href="`/${platform.slug}/discussion-forums/channel/${data.slug}`" class="text-nerve underline" target="_blank">View Channel <Icon as="external-link" class="text-xs" /></a>
                            </div>
                        </div>

                        <div v-if="data.metadata.rss_feed.active && ingestion && ingestion.progress" class="relative flex-1 h-6 bg-dust-200 rounded overflow-hidden">
                            <div 
                                class="absolute top-0 left-0 h-full bg-dust-400 transition-all duration-300 ease-out"
                                :style="{ width: `${ingestion.progress.percentage || 0}%` }"
                            ></div>
                            <span class="absolute left-2 top-[3px] text-xs text-dust-800">
                                {{ ingestion.message || 'Processing...' }}
                            </span>
                            <span class="absolute top-[3px] right-2 text-xs text-dust-800">
                                {{ ingestion.progress.percentage || 0 }}%
                            </span>
                        </div>

                        <p class="text-sm text-gray-600 italic mt-1">
                            If you have an RSS feed enabled, each podcast/article/paper becomes a Topic. Only you can create new Topics in this channel (perhaps for a channel note), but all users can create posts and replies in it.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>